/** Human-readable labels for every scorecard metric key. */
export const SCORECARD_DISPLAY_NAME: Record<string, string> = {
  prs_merged: 'PRs merged',
  ttv_avg_seconds: 'Avg TTV',
  auto_approval_rate: 'Auto-approval rate',
  revert_rate: 'Revert rate (7d on bot-approved)',
  override_rate: 'Override rate (/request-jedi-review)',
  ttfr_avg_seconds: 'Avg TTFR',
  findings_acted_on_rate: 'Findings acted-on',
  avg_cost_per_pr: 'Avg cost per PR',
  tokens_in_per_day: 'Council tokens / day',
  cache_read_share: 'Cache-read share',
};

/**
 * Per-metric hover-tooltip text. Surfaces on a small (i) glyph next to the
 * scorecard tile title via the browser's native title= tooltip. Kept short —
 * longer concept writeups live on /help.
 */
export const SCORECARD_METRIC_HELP: Record<string, string> = {
  prs_merged: 'Number of PRs merged in the selected range. Bars show daily counts across that same range.',
  auto_approval_rate: 'Of all unique PRs Diff Vader reviewed in the selected range (live mode), the share whose final verdict was APPROVE — i.e. cleared without requiring a human reviewer. Each PR counts once; a PR that went REQUEST_CHANGES then APPROVE still counts. NEEDS_HUMAN and TOO_LARGE PRs stay in the denominator. Dashed line marks the 35% target. Sparkline shows artifact-level APPROVE% (each DV run counted separately); the tile counts each PR once by its final verdict.',
  ttv_avg_seconds: 'Average time-to-verdict (mean of daily p50s): from PR ready-for-review to Diff Vader posting its verdict, averaged over the selected range.',
  revert_rate: 'Of bot-approved PRs that merged in the selected range, percent that were later reverted within 7 days. PRs merged in the last ~7 days may not yet have their settlement data populated.',
  override_rate: 'Of unique PRs Diff Vader reviewed in the selected range, percent where a human ran /request-jedi-review to request a human reviewer. Unlike the other “lower is better” tiles this one has a floor as well as a ceiling: a rate of exactly zero across 50+ uniquely reviewed PRs shows yellow, because nobody ever asking for a human is the over-reliance signal the risk assessment (RR-95 F8) exists to catch, not a clean bill of health. Under 50 unique PRs no color is shown — a quiet month says nothing either way.',
  ttfr_avg_seconds: 'Average time-to-first-review (mean of daily p50s): from PR ready-for-review to first human review comment.',
  findings_acted_on_rate: 'Of substantive DV review comments in the selected range, percent whose anchored line was modified before merge (GitHub reports position=null). Early-rollout numbers will look low — this measures forward-only behavior change, not quality.',
  avg_cost_per_pr: 'Average Diff Vader spend per unique PR reviewed in live mode in the selected range. Sums cost_usd across all council runs for the PR (including reruns); divides by the PRs that actually carry cost data, which is fewer than the Auto-approval card’s denominator on any range reaching back before cost capture started — the tile says so when it happens. Lower is better. Target line is a stretch goal — current mean is dragged by a long tail of expensive PRs. Sparkline shows daily average over the selected range.',
  tokens_in_per_day: 'Total council input tokens per day (input + cache reads + cache writes). Baseline is the steady-state post-caching level (a pinned 2-week window); a figure over the selected range creeping above it flags a regression. Lower is better.',
  cache_read_share: 'Of council input tokens on caching-enabled (SDK-path) runs in the selected range, share served from cache (cache reads are excluded from the per-minute quota and cost ~10%). Runs predating the caching rollout are excluded from the denominator so they don’t dilute the share. Baseline is the steady-state post-caching share (a pinned 2-week window, token-weighted); higher than baseline means utilization is improving.',
};

/**
 * Caption for a scorecard tile and its sparkline aria-label, describing the
 * window the server actually served.
 *
 * "Last Nd" is a claim about recency, not just about width: a custom range of
 * 2026-06-01 → 2026-06-30 is thirty days wide but ended eleven weeks ago, and
 * captioning every tile "Last 30d" misdates the evidence. PeriodPicker's
 * presetForRange gates on `to === today` for exactly this reason; this gates
 * the same way. A tile caption is what survives into a screenshot, so it has
 * to carry the end date when the window is historical.
 */
export function scorecardWindowLabel(
  window: { to?: string; days?: number } | undefined,
  today: string,
): string {
  const days = window?.days;
  if (days == null) return 'Selected';
  const to = window?.to;
  if (!to) return `${days}d`;
  if (to !== today) return `${days}d ending ${to}`;
  return [7, 30, 90].includes(days) ? `Last ${days}d` : `${days}d`;
}

/** Formats a ratio as a percentage with 1 decimal place (0.05 → "5.0%").
 *  Null/undefined → em dash. `undefined` reaches here when the API and
 *  portal are deployed independently and the API omits a field this build
 *  doesn't know about — it must degrade exactly like `null`, not produce
 *  "NaN%". */
export function fmtPercent(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

/** Formats USD with two decimals ($1.23). Null/undefined/NaN/Infinity → em dash. */
export function fmtUsd(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `$${v.toFixed(2)}`;
}

/** Formats a large count compactly (21,416,331 → "21.4M") so big token totals
 *  fit a metric tile (baseline / current / delta pill) without overflowing.
 *  Null/undefined/NaN/Infinity → em dash. */
export function fmtCompact(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(v);
}

/** Formats a duration in seconds as "Xh Ym" / "Xm Ys" / "Xs". Null/undefined/
 *  NaN/Infinity/negative → em dash. */
export function fmtSeconds(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v) || v < 0) return '—';
  const total = Math.round(v);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

const PERCENT_METRICS = new Set([
  'auto_approval_rate',
  'revert_rate',
  'override_rate',
  'findings_acted_on_rate',
  'cache_read_share',
]);

const DURATION_SECONDS_METRICS = new Set([
  'ttv_avg_seconds',
  'ttfr_avg_seconds',
]);

const CURRENCY_METRICS = new Set([
  'avg_cost_per_pr',
]);

// Large counts shown compactly so they fit a tile (token totals run into the
// tens of millions; the raw number overflowed the card + delta pill).
const COMPACT_METRICS = new Set([
  'tokens_in_per_day',
]);

/** Returns the appropriate formatter for a scorecard metric key, or undefined for plain numeric. */
export function metricFormat(metric: string): ((v: number | null | undefined) => string) | undefined {
  if (PERCENT_METRICS.has(metric)) return fmtPercent;
  if (DURATION_SECONDS_METRICS.has(metric)) return fmtSeconds;
  if (CURRENCY_METRICS.has(metric)) return fmtUsd;
  if (COMPACT_METRICS.has(metric)) return fmtCompact;
  return undefined;
}
